import React, { useCallback } from 'react';
import { Box, Button, Avatar, IconButton, Typography, CircularProgress } from '@mui/material';
import { CloudUploadRounded as UploadIcon, DeleteRounded as DeleteIcon } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { GET_CLOUDINARY_CONFIG } from '../apollo/queries';
import { getAvatarUrl } from '../utils/cloudinary';

/**
 * Cloudinary Upload Widget component for profile pictures
 * 
 * @param {string} value - Current public ID or URL
 * @param {function} onChange - Called with new public ID when upload completes
 * @param {string} personName - Person's name for alt text and folder organization
 */
function CloudinaryUpload({ value, onChange, personName = 'person' }) {
  const { data: configData, loading: configLoading } = useQuery(GET_CLOUDINARY_CONFIG);
  const config = configData?.cloudinaryConfig;

  const openWidget = useCallback(() => {
    if (!config?.cloudName || !config?.uploadPreset) {
      console.error('Cloudinary config not loaded');
      return;
    }

    // eslint-disable-next-line no-undef
    const widget = cloudinary.createUploadWidget(
      {
        cloudName: config.cloudName,
        uploadPreset: config.uploadPreset,
        sources: ['local', 'camera', 'url'],
        multiple: false,
        maxFiles: 1,
        cropping: true,
        croppingAspectRatio: 1,
        croppingShowDimensions: true,
        croppingCoordinatesMode: 'custom',
        showSkipCropButton: false,
        folder: 'family-tree/profile-pictures',
        resourceType: 'image',
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'],
        maxImageFileSize: 10000000, // 10MB
        theme: 'minimal',
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#2E7D32',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#2E7D32',
            action: '#FF8F00',
            inactiveTabIcon: '#0E2F5A',
            error: '#F44235',
            inProgress: '#2E7D32',
            complete: '#2E7D32',
            sourceBg: '#E4EBF1',
          },
        },
      },
      (error, result) => {
        if (error) {
          console.error('Upload error:', error);
          return;
        }
        if (result.event === 'success') {
          // Store just the public_id, not the full URL
          onChange(result.info.public_id);
          widget.close();
        }
      }
    );

    widget.open();
  }, [config, onChange]);

  const handleRemove = () => {
    onChange('');
  };

  const avatarUrl = value ? getAvatarUrl(value, 100) : null;

  if (configLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar
        src={avatarUrl}
        alt={personName}
        sx={{ width: 80, height: 80, bgcolor: 'grey.200' }}
      />
      <Box>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={openWidget}
          size="small"
          disabled={!config?.cloudName}
        >
          {value ? 'Change Photo' : 'Upload Photo'}
        </Button>
        {value && (
          <IconButton
            onClick={handleRemove}
            size="small"
            color="error"
            sx={{ ml: 1 }}
            title="Remove photo"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        {!config?.cloudName && (
          <Typography variant="caption" color="error" display="block">
            Cloudinary not configured
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default CloudinaryUpload;

